import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik, Field } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import i18n from 'i18n-js';
import { Grid, Typography, Box } from '@material-ui/core';

import { userGroupsActions } from 'state/modules/userGroups';
import { TextField } from 'components/FormFields';

import { BaseModal } from 'components/UI';
import { MultipleVehicleGroupsSelect } from 'components/AsyncFields';

const UpdateDealerModal = ({ id, userGroup, errors, open, handleClick }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();

  const formSchema = Yup.object().shape({
    name: Yup.string().required(i18n.t('common.required_field')),
    state: Yup.string().required(i18n.t('common.required_field')),
    default_vehicle_group_id: Yup.array(Yup.object().shape({ id: Yup.number() })),
    email: Yup.string().required(i18n.t('common.required_field')),
    zip_code: Yup.string().required(i18n.t('common.required_field')),
    address: Yup.string().required(i18n.t('common.required_field')),
    city: Yup.string().required(i18n.t('common.required_field')),
    phone_number: Yup.string(),
  });

  const { loading, updateUserGroupSuccess } = useSelector((state) => state.userGroups);

  const initialValues = {
    name: userGroup?.name || '',
    default_vehicle_group_id: userGroup?.default_vehicle_group || [],
    state: userGroup?.state || '',
    email: userGroup?.email || '',
    address: userGroup?.address || '',
    zip_code: userGroup?.zip_code || '',
    city: userGroup?.city || '',
    phone_number: userGroup?.phone_number || '',
  };

  const onSubmit = (values) => {
    const payload = {
      ...values,
      id,
      default_vehicle_group_id: values.default_vehicle_group_id.map((vg) => vg.id),
    };

    // remove empty fields
    Object.keys(payload).forEach(
      (key) => key !== 'keyword' && !payload[key] && delete payload[key]
    );

    dispatch(userGroupsActions.fetchCreateUserGroup(payload, 'business', 'details', true));
  };

  useEffect(() => {
    if (updateUserGroupSuccess) {
      if (id) {
        dispatch(userGroupsActions.fetchUserGroupById(id, 'details', 'business'));
        dispatch(userGroupsActions.resetUpdateUserGroup());
        handleClick();
      }
    }
  }, [id, updateUserGroupSuccess, handleClick, dispatch]);

  const handleSubmit = () => {
    formRef.current.submitForm();
  };

  return (
    <BaseModal
      size="md"
      open={open}
      hasEnterButton
      hasCancelButton
      onEnter={handleSubmit}
      onCancel={handleClick}
      closeButtonType="icon"
      enableCloseButton={false}
      loading={loading}
      title={i18n.t('user_groups.update_dealer')}
      enterButtonLabel={i18n.t('common.update')}
      privileges={{ action: 'update', subject: 'user_group' }}
      enterButtonProps={{ 'data-cy': 'save-user-group' }}
    >
      <Grid container direction="row" justifyContent="center" alignItems="center">
        <Grid item xs={12}>
          <Formik
            innerRef={formRef}
            initialValues={initialValues}
            validationSchema={formSchema}
            enableReinitialize={true}
            onSubmit={onSubmit}
          >
            {({ handleSubmit, values }) => (
              <form noValidate onSubmit={handleSubmit}>
                <Grid
                  container
                  spacing={1}
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                >
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      <Box fontWeight="fontWeightBold" fontSize={14} mb={1} mt={2}>
                        {i18n.t('user_groups.dealer_basic_info')}
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Field
                      type="text"
                      name="name"
                      component={TextField}
                      asyncErrors={errors?.name}
                      label={i18n.t('common.name')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Field
                      type="text"
                      name="address"
                      component={TextField}
                      asyncErrors={errors?.address}
                      label={i18n.t('common.address')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Field
                      type="text"
                      name="state"
                      component={TextField}
                      asyncErrors={errors?.state}
                      label={i18n.t('common.state')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Field
                      type="text"
                      name="email"
                      component={TextField}
                      asyncErrors={errors?.email}
                      label={i18n.t('fields.email')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Field
                      type="text"
                      name="city"
                      component={TextField}
                      asyncErrors={errors?.city}
                      label={i18n.t('common.city')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Field
                      type="text"
                      name="zip_code"
                      component={TextField}
                      asyncErrors={errors?.zip_code}
                      label={i18n.t('common.zip_code')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Field
                      type="text"
                      name="phone_number"
                      component={TextField}
                      required={false}
                      asyncErrors={errors?.phone_number}
                      label={i18n.t('common.phone')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      <Box fontWeight="fontWeightBold" fontSize={14} mb={1} mt={4}>
                        {i18n.t('common.vehicle_group')}
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={10} md={6}>
                    <Field
                      name="default_vehicle_group_id"
                      component={MultipleVehicleGroupsSelect}
                      asyncErrors={errors?.default_vehicle_group_id}
                      defaultVehicleGroup={userGroup?.default_vehicle_group}
                      label={i18n.t('user_groups.select_vehicle_group')}
                    />
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Grid>
      </Grid>
    </BaseModal>
  );
};

UpdateDealerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  userGroup: PropTypes.any,
  handleClick: PropTypes.func.isRequired,
};

UpdateDealerModal.defaultProps = {
  open: false,
  userGroup: null,
  handleClick: () => {},
};

export default UpdateDealerModal;
